import { getOnStorage } from '@/shared';
import { DataSetsProps } from '@/types';
import { useCallback, useRef, type Dispatch, type SetStateAction } from 'react';
import { normalizeDatasetType } from './_syncCommons';
import { formatFileSize } from '@/shared/commonFunctions';
import { useDbFunctions } from '../dbFunctions/useDBFunctions';
import { useEvents, getDateTimeMS } from '../use-events/useEvents';
import {
  clearImageDatasetMeta,
  createDatasetWriteSession,
  writeDatasetFile,
  writeImageDatasetFile,
  type DatasetWriteResult,
} from '@/shared/bfSeller';
import {
  _memTune,
  getDatasetKey,
  DatasetWithStoreMeta,
  updateProgress,
  PROGRESS_THROTTLE_MS,
  SPEED_UPDATE_INTERVAL_MS,
  progressLastCall,
} from './_syncCommons';

type DownloadEventProps = Record<string, { status: 0 | 1; arquivo?: string; dataFim?: string; dataInicio?: string }>;

/**
 * Tipos cujo payload continua em memória depois de gravado.
 *
 * Todo arquivo é gravado assim que é parseado e descartado em seguida — é o que impede o run
 * inteiro (dezenas de MB por loja) de ficar vivo na heap até o fim do sync. Os poucos que ficam
 * são objetos pequenos que o `_usePrepareFiles` repassa direto para contextos do React.
 *
 * `imagens` **não** está aqui: o dicionário de base64 era o maior payload do run e ficava vivo
 * até o fim, quando era gravado de uma vez. Agora o zip vai inteiro para o `BF_SELLER_IMAGES`
 * assim que o download termina, sem passar por memória imagem a imagem.
 */
const KEEP_PAYLOAD_TYPES = new Set([
  'campanha_produto',
  'campanha_cliente',
  'campanhaClientes',
  'campanha_faixa',
  'campanha_mix_fornecedor',
  'indicador_loja',
  'indicadores',
]);

/**
 * O que sobra de um arquivo depois de gravado. Sem payload: o conteúdo já está no `BF_SELLER_DATASETS` e
 * os poucos objetos que alimentam contexto foram entregues pelo `onPersisted`, na hora.
 */
type DatasetDownloadResult = {
  name: string;
  type: string;
  datasetKey: string;
  // dataset: DatasetWithStoreMeta;
  write: DatasetWriteResult;
};

type FetchDatasetsResponse = {
  byDatasetKey: Record<string, DatasetDownloadResult>;
};

/**
 * Chamado logo depois de cada arquivo ser gravado, antes de o próximo começar.
 *
 * É o que fecha o ciclo por arquivo: relatório, registro do download e alimentação dos contextos
 * acontecem aqui, e não numa varredura no fim do run. Se lançar, o arquivo é marcado como falha
 * — não derruba os demais.
 */
export type OnDatasetPersisted = (info: {
  dataset: DatasetWithStoreMeta;
  type: string;
  write: DatasetWriteResult;
  /** Payload, só para os tipos de `KEEP_PAYLOAD_TYPES`. */
  data: any;
}) => void | Promise<void>;

const MAX_CONCURRENT_DOWNLOADS = _memTune.maxConcurrentDownloads;

const runWithConcurrency = async <T>(tasks: (() => Promise<T>)[], max: number): Promise<PromiseSettledResult<T>[]> => {
  const results: PromiseSettledResult<T>[] = [];
  let index = 0;
  const runNext = async (): Promise<void> => {
    const i = index++;
    if (i >= tasks.length) return;
    try {
      const value = await tasks[i]();
      results[i] = { status: 'fulfilled', value };
    } catch (reason) {
      results[i] = { status: 'rejected', reason };
    }
    await runNext();
  };
  const workers = Array.from({ length: Math.min(max, tasks.length) }, () => runNext());
  await Promise.all(workers);
  return results;
};

/** Loja/segmento do descritor para o registro do zip — vêm como número, string ou ausentes. */
const toNumeroOuNulo = (value: unknown): number | null => {
  if (value == null || String(value).trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const isZipContent = (resp: Response, url: string) => {
  const ct = resp.headers.get('content-type') || '';
  return ct.includes('zip') || url.toLowerCase().endsWith('.zip');
};

/**
 * Quanto tempo um download pode ficar sem receber byte nenhum antes de ser dado como parado.
 *
 * É prazo de **inatividade**, não de duração: cada chunk que chega reinicia a contagem, então um
 * arquivo grande numa conexão lenta mas viva termina normalmente.
 */
export const DOWNLOAD_IDLE_TIMEOUT_MS = 30000;

const DOWNLOAD_STALLED = 'DownloadStalledError';

export const isDownloadStalled = (error: unknown): boolean => (error as Error)?.name === DOWNLOAD_STALLED;

export type DownloadWatchdog = {
  signal: AbortSignal;
  /** Espera `promise` por no máximo `idleMs`; estourou, aborta o fetch e rejeita. */
  guard: <T>(promise: Promise<T>) => Promise<T>;
};

/**
 * Vigia um download: o `fetch` e cada `reader.read()` passam pelo `guard`.
 *
 * Sem isso, uma conexão que cai no meio do corpo (rede móvel, aparelho que dorme) deixa o
 * `reader.read()` pendente para sempre — nem resolve, nem rejeita — e o `runWithConcurrency`
 * espera por ele, pendurando o sync inteiro e quem estiver esperando por ele (o "Entrar com esse
 * cliente"). O `abort` fecha a conexão; a rejeição do `guard` é o que garante a saída, mesmo onde
 * o abort não chega ao stream.
 */
export const createDownloadWatchdog = (name: string, idleMs = DOWNLOAD_IDLE_TIMEOUT_MS): DownloadWatchdog => {
  const controller = new AbortController();

  const guard = <T>(promise: Promise<T>): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        const error = new Error(`Download de ${name} parado há ${idleMs / 1000}s`);
        error.name = DOWNLOAD_STALLED;
        controller.abort();
        reject(error);
      }, idleMs);

      promise.then(
        (value) => {
          clearTimeout(timer);
          resolve(value);
        },
        (error) => {
          clearTimeout(timer);
          reject(error);
        }
      );
    });

  return { signal: controller.signal, guard };
};

/**
 * Uma linha por arquivo com o tempo de cada fase. Existe para localizar o gargalo do sync sem
 * adivinhação: `download` vs `parse` vs `write` (e, nas imagens, `zipWrite`).
 */
const logTempos = (
  name: string,
  info: {
    bytes?: number;
    download: number;
    parse?: number;
    zipWrite?: number;
    write?: { rows: number; timing?: Record<string, number> };
    total: number;
  }
) => {
  const partes = [
    `total ${info.total}ms`,
    `download ${info.download}ms`,
    info.parse != null ? `parse ${info.parse}ms` : null,
    info.zipWrite != null ? `grava zip ${info.zipWrite}ms` : null,
    info.write ? `${info.write.rows} linhas` : null,
    info.write?.timing
      ? `write ${info.write.timing.total}ms (norm ${info.write.timing.normalize} / purge ${info.write.timing.purge} / put ${info.write.timing.put})`
      : null,
    info.bytes ? `${Math.round(info.bytes / 1024)}KB` : null,
  ].filter(Boolean);

  console.log(`[sync][${name}] ${partes.join(' · ')}`);
};

const getDatasetProgressLabel = (dataset: DatasetWithStoreMeta) => {
  const base = dataset.DESC_ARQUIVO || normalizeDatasetType(dataset.TIPO_ARQUIVO);
  const loja = dataset.COD_LOJA != null && String(dataset.COD_LOJA).trim() !== '' ? String(dataset.COD_LOJA) : null;
  const segmento =
    dataset.COD_SEGMENTO != null && String(dataset.COD_SEGMENTO).trim() !== '' ? String(dataset.COD_SEGMENTO) : null;

  if (!loja && !segmento) return base;
  if (loja && segmento) return `${base} - Loja ${loja} / Segmento ${segmento}`;
  if (loja) return `${base} - Loja ${loja}`;
  return `${base} - Segmento ${segmento}`;
};

const isPlainObject = (value: any): value is Record<string, any> =>
  value != null && typeof value === 'object' && !Array.isArray(value);

const normalizeStockItem = (item: any) => {
  if (!isPlainObject(item)) return null;
  const codProdutoRaw = item.COD_PRODUTO ?? item.cod_produto;
  const qtdEstoqueRaw = item.QTD_ESTOQUE ?? item.qtd_estoque;
  if (codProdutoRaw == null || qtdEstoqueRaw == null) return null;

  const codProduto = Number(codProdutoRaw);
  const qtdEstoque = Number(qtdEstoqueRaw);
  if (!Number.isFinite(codProduto) || !Number.isFinite(qtdEstoque)) return null;

  const codLojaRaw = item.COD_LOJA ?? item.cod_loja;
  const codLoja = codLojaRaw == null || codLojaRaw === '' ? null : Number(codLojaRaw);

  return {
    ...item,
    COD_PRODUTO: codProduto,
    QTD_ESTOQUE: qtdEstoque,
    COD_LOJA: Number.isFinite(codLoja as number) ? (codLoja as number) : null,
  };
};

const normalizeStockData = (payload: any): any[] => {
  if (payload == null) return [];

  const stack: any[] = [payload];
  const seen = new Set<any>();
  const stockItems: any[] = [];

  while (stack.length) {
    const current = stack.pop();
    if (current == null || seen.has(current)) continue;
    if (typeof current === 'object') seen.add(current);

    if (Array.isArray(current)) {
      current.forEach((item) => {
        const normalizedItem = normalizeStockItem(item);
        if (normalizedItem) stockItems.push(normalizedItem);
        else if (item && typeof item === 'object') stack.push(item);
      });
      continue;
    }

    if (isPlainObject(current)) {
      Object.values(current).forEach((value) => {
        if (value != null && (Array.isArray(value) || isPlainObject(value))) stack.push(value);
      });
    }
  }

  return stockItems;
};

const normalizeParametroPrecoBaseData = (payload: any): any => {
  if (payload == null) return {};
  if (Array.isArray(payload)) return payload;
  if (!isPlainObject(payload)) return payload;

  const candidates = [payload?.data, payload?.data?.data, payload].filter(isPlainObject) as Record<string, any>[];
  const dataRoot = candidates.find((candidate) => Object.keys(candidate).some((key) => Number.isFinite(Number(key))));

  return dataRoot || payload;
};

type UseDownloadFilesProps = {
  isSuccess: boolean;
  isOpenedModal: boolean;
  setDownloadSpeedBpsRef: React.MutableRefObject<(bps: number) => void>;
  setError: (error: boolean) => void;
  setIsSuccess: (isSuccess: boolean) => void;
  setFileDownload: (fileDownload: string) => void;
  setProgressPercent: (progressPercent: number) => void;
  setDownloadSpeedBps: (downloadSpeedBps: number) => void;
  setDownloadingFiles: (downloadingFiles: string[]) => void;
  setIsDownloadingFiles: Dispatch<SetStateAction<string[]>>;
  setDownloadingFileName: (downloadingFileName: string) => void;
  setStartDownloadingFile: (startDownloadingFile: string) => void;
  setFileDownloadProgress: (fileDownloadProgress: string) => void;
};

export const useFetchFiles = (_useStates: UseDownloadFilesProps) => {
  const displayLeaderRef = useRef<string | null>(null);

  const db = useDbFunctions();
  const { registerEvent } = useEvents(db);

  const fetchDatasets = useCallback(
    async (list?: DataSetsProps[], onPersisted?: OnDatasetPersisted) => {
      var totalSize = 0;
      var downloadedSize = 0;
      var speedLastTime = Date.now();
      var speedLastBytes = 0;
      _useStates.setDownloadSpeedBpsRef.current(0);
      const downloadEvents = {} as DownloadEventProps;
      // Uma sessão por run: o escopo de cada dataset é apagado uma vez só, por mais arquivos que
      // o backend mande para a mesma (loja, segmento).
      const writeSession = createDatasetWriteSession();
      // Mesma regra para as imagens: a store é limpa no primeiro arquivo do run, não em cada um.
      let imagensStoreLimpa = false;
      let imagensZipStoreLimpa = false;

      /**
       * Grava um lote de imagens. O `clear` da store acontece uma vez por sync, no primeiro lote —
       * fazer por arquivo apagaria o que o arquivo anterior gravou.
       */
      const flushImagens = async (lote: Record<number, Blob>): Promise<number> => {
        if (!Object.keys(lote).length) return 0;

        const primeiraLeva = !imagensStoreLimpa;
        // A store de imagens é zerada na primeira leva do run; o registro dos zips antigos sai
        // junto, senão o sync seguinte pularia o download de conteúdo que já não existe.
        if (primeiraLeva) await clearImageDatasetMeta();

        const rows = await db.setImagesOnDB(lote, { reset: primeiraLeva });
        imagensStoreLimpa = true;
        return rows;
      };

      /**
       * Guarda o zip como veio, sem abrir.
       *
       * O sync não descompacta mais: era a descompactação — abrir o zip, converter cada entrada em
       * `Blob` e gravar imagem por imagem — que dominava o tempo do arquivo de imagens, e nada
       * disso precisa acontecer durante o download. Aqui é um `put` só, do corpo da resposta.
       *
       * O `clear` da store acontece uma vez por run, no primeiro zip, pelo mesmo motivo do purge
       * dos datasets; o registro dos zips antigos sai junto, senão o sync seguinte pularia o
       * download de conteúdo que já não existe.
       */
      const gravarZipImagens = async (dataset: DatasetWithStoreMeta, type: string, zip: Blob): Promise<boolean> => {
        const primeiroZip = !imagensZipStoreLimpa;
        if (primeiroZip) await clearImageDatasetMeta();

        const ok = await db.setImagesZipOnDB(
          {
            url: dataset?.URL_S3 ?? '',
            zip,
            tipo: type,
            loja: toNumeroOuNulo(dataset?.COD_LOJA),
            segmento: toNumeroOuNulo(dataset?.COD_SEGMENTO),
            bytes: zip.size,
            dtUpdate: dataset?.DT_UPDATE,
          },
          { reset: primeiroZip }
        );

        imagensZipStoreLimpa = true;
        return ok;
      };

      /**
       * Grava o arquivo recém-baixado e devolve o payload só se alguém ainda precisar dele.
       *
       * Datasets vão para o `BF_SELLER_DATASETS`; imagens seguem no `BF_SELLER_IMAGES`, mas gravadas
       * aqui e não no fim do run. Nos dois casos o payload é solto logo depois.
       */
      const persistDataset = async (
        type: string,
        dataset: DatasetWithStoreMeta,
        data: any,
        opts: { arquivosGravados?: number } = {}
      ): Promise<Pick<DatasetDownloadResult, 'write'>> => {
        let write = await writeDatasetFile({ tipo: type, dataset, payload: data, session: writeSession });
        let kept: any = null;

        if (write.status === 'imagens') {
          // O zip já foi gravado inteiro logo depois do download; aqui só se reporta o resultado.
          if (opts.arquivosGravados != null) {
            write = { ...write, ok: opts.arquivosGravados > 0, rows: opts.arquivosGravados };
          } else {
            try {
              const rows = await flushImagens(data);
              write = { ...write, rows };
            } catch (error: any) {
              write = { ...write, ok: false, status: 'erro', error: error?.message ?? String(error) };
            }
          }

          // Mesmo registro dos outros arquivos: é dele que sai o "este zip já veio" do próximo sync.
          if (write.ok && write.rows > 0) await writeImageDatasetFile({ tipo: type, dataset, rows: write.rows });
        } else {
          if (!write.ok) {
            console.warn(
              `[BF_SELLER_DATASETS] ${type} loja=${write.scope.loja} seg=${write.scope.segmento}: ${write.status}`,
              write.error ?? ''
            );
          }
          kept = KEEP_PAYLOAD_TYPES.has(type) ? data : null;
        }

        /**
         * Ponto único de aviso: relatório, registro do download e contextos são resolvidos aqui,
         * arquivo por arquivo, antes de o próximo começar. Nada disso sobra para uma varredura no
         * fim do run — era ela que mantinha o modal em "Aguarde" depois do último byte.
         *
         * Vale para imagens também: sem o registro, elas voltam a ser baixadas no sync seguinte.
         */
        if (onPersisted) {
          try {
            await onPersisted({ dataset, type, write, data: kept });
          } catch (error) {
            console.warn(`[sync] falha ao processar ${type} depois da gravação:`, error);
          }
        }

        return { write };
      };

      const fetchAndSaveJson = async (
        url: string,
        name: string,
        desc: string,
        datasetKey: string,
        type: string,
        dataset: DatasetWithStoreMeta,
        skipZipRedirect = false
      ): Promise<DatasetDownloadResult> => {
        if (!displayLeaderRef.current) displayLeaderRef.current = desc;
        if (displayLeaderRef.current === desc) {
          _useStates.setDownloadingFileName(desc);
          _useStates.setFileDownloadProgress(formatFileSize(0));
        }

        downloadEvents[name] = { ...(downloadEvents[name] || {}), arquivo: name, dataInicio: getDateTimeMS(), status: 1 };
        _useStates.setStartDownloadingFile(name);

        const tArquivo = Date.now();
        let tDownloadFim = tArquivo;

        try {
          _useStates.setIsDownloadingFiles((prev) => [...prev, name]);
          const watchdog = createDownloadWatchdog(name);
          const resp = await watchdog.guard(fetch(url, { cache: 'no-store', signal: watchdog.signal }));

          if (!skipZipRedirect && isZipContent(resp, url)) {
            console.warn(`[${name}] conteúdo ZIP detectado, redirecionando para fetchAndSaveImages`);
            // Reutiliza a mesma Response — evita segundo fetch do mesmo ZIP (corpo ainda não foi lido).
            return await fetchAndSaveImages(resp, watchdog, name, desc, url, datasetKey, type, dataset);
          }

          if (resp.status === 403) {
            await registerEvent({
              propsNameUuid: name,
              offline: true,
              stEvento: 0,
              tipoEvento: 'DATASET_EXPIRADO',
              obsEvento: `Dataset expired: ${name}`,
            }).catch(() => {});
            throw new Error(`Failed to fetch ${name}: ${resp.statusText}`);
          } else if (resp.status >= 300 || resp.status < 200) {
            await registerEvent({
              propsNameUuid: name,
              offline: true,
              stEvento: 0,
              tipoEvento: 'DOWNLOAD',
              obsEvento: `Failed to fetch ${name}: ${resp.statusText}`,
            }).catch(() => {});
            throw new Error(`Failed to fetch ${name}: ${resp.statusText}`);
          }

          const contentLength = Number(resp.headers.get('content-length')) || 0;
          totalSize += contentLength;
          let totalArchive = contentLength;
          let downloadedArchive = 0;
          let lastFileDownloadUpdate = 0;

          if (displayLeaderRef.current === desc) {
            _useStates.setFileDownload(formatFileSize(totalArchive));
            _useStates.setFileDownloadProgress(formatFileSize(0));
            _useStates.setProgressPercent(0);
          }

          const reader = resp?.body?.getReader();
          if (!reader) throw new Error('Failed to get response body');
          const decoder = new TextDecoder('utf-8');
          const chunks: string[] = [];

          while (true) {
            const result = await watchdog.guard(reader.read());
            const { done, value } = result;
            if (done) break;
            const len = value?.byteLength ?? 0;
            downloadedSize += len || 1;
            downloadedArchive += len || 1;
            const now = Date.now();
            if (now - speedLastTime >= SPEED_UPDATE_INTERVAL_MS) {
              const elapsed = (now - speedLastTime) / 1000;
              if (elapsed > 0) {
                const bps = (downloadedSize - speedLastBytes) / elapsed;
                _useStates.setDownloadSpeedBpsRef.current(bps);
              }
              speedLastTime = now;
              speedLastBytes = downloadedSize;
            }
            if (!displayLeaderRef.current) {
              displayLeaderRef.current = desc;
              _useStates.setDownloadingFileName(desc);
            }
            if (now - lastFileDownloadUpdate >= PROGRESS_THROTTLE_MS && displayLeaderRef.current === desc) {
              lastFileDownloadUpdate = now;
              _useStates.setFileDownload(formatFileSize(totalArchive));
              _useStates.setFileDownloadProgress(formatFileSize(downloadedArchive));
            }
            updateProgress(downloadedArchive, totalArchive, name);
            if (displayLeaderRef.current === desc) updateProgress(downloadedArchive, totalArchive, 'progress');
            if (len) chunks.push(decoder.decode(value, { stream: true }));
          }
          progressLastCall[name] = 0;
          progressLastCall['progress'] = 0;
          if (displayLeaderRef.current === desc) {
            _useStates.setFileDownload(formatFileSize(totalArchive));
            _useStates.setFileDownloadProgress(formatFileSize(downloadedArchive));
            updateProgress(downloadedArchive, totalArchive, 'progress');
          }
          updateProgress(downloadedArchive, totalArchive, name);

          if (displayLeaderRef.current === desc) displayLeaderRef.current = null;

          tDownloadFim = Date.now();
          const tParse = Date.now();
          let json = JSON.parse(chunks.join(''));
          chunks.length = 0;
          let data = json?.catalogo ?? json?.preco ?? json;
          json = null;

          if (name === 'imagens') {
            data = typeof data === 'string' ? JSON.parse(data) : data;
            const out: Record<number, string> = {};
            if (Array.isArray(data)) {
              for (let j = 0; j < data.length; j++) {
                const item = data[j];
                out[item.COD_PRODUTO] = item.imagem?.replace(/^data:.*;base64,/i, '');
              }
            }
            data = out;
          }
          if (typeof data === 'string') data = JSON.parse(data);
          if (type === 'estoque') data = normalizeStockData(data);
          if (type === 'parametro_preco_base') data = normalizeParametroPrecoBaseData(data);
          const msParse = Date.now() - tParse;

          // Grava, processa e solta a referência antes de o próximo arquivo começar. Só um
          // payload parseado existe por vez — segurar o run inteiro é o que estoura a memória.
          const persisted = await persistDataset(type, dataset, data);
          data = null;

          logTempos(name, {
            bytes: totalArchive,
            download: tDownloadFim - tArquivo,
            parse: msParse,
            write: persisted.write,
            total: Date.now() - tArquivo,
          });

          downloadEvents[name] = { ...(downloadEvents[name] || {}), dataFim: getDateTimeMS(), status: 1 };
          return { ...persisted, name, datasetKey, type };
        } catch (error) {
          if (displayLeaderRef.current === desc) displayLeaderRef.current = null;
          console.error(`Error fetching ${name}:`, error);
          _useStates.setError(true);
          downloadEvents[name] = { ...(downloadEvents[name] || {}), dataFim: getDateTimeMS(), status: 0 };
          throw error;
        }
      };

      const fetchAndSaveImages = async (
        resp: Response,
        watchdog: DownloadWatchdog,
        name: string,
        desc: string,
        url: string,
        datasetKey: string,
        type: string,
        dataset: DatasetWithStoreMeta
      ): Promise<DatasetDownloadResult> => {
        if (!displayLeaderRef.current) displayLeaderRef.current = desc;
        if (displayLeaderRef.current === desc) {
          _useStates.setDownloadingFileName(desc);
          _useStates.setFileDownloadProgress(formatFileSize(0));
        }
        downloadEvents[name] = { ...(downloadEvents[name] || {}), arquivo: name, dataInicio: getDateTimeMS(), status: 1 };

        _useStates.setStartDownloadingFile(name);

        const tArquivo = Date.now();
        let tDownloadFim = tArquivo;

        try {
          if (!resp.ok) throw new Error(`Failed to fetch imagens zip: ${resp.statusText}`);
          if (!isZipContent(resp, url)) throw new Error('Conteúdo não é ZIP, fallback para JSON');

          const contentLength = Number(resp.headers.get('content-length')) || 0;
          totalSize += contentLength;
          let totalArchive = contentLength;
          let downloadedArchive = 0;
          let lastFileDownloadUpdate = 0;

          if (displayLeaderRef.current === desc) {
            _useStates.setFileDownload(formatFileSize(totalArchive));
            _useStates.setFileDownloadProgress(formatFileSize(0));
            _useStates.setProgressPercent(0);
          }

          const reader = resp.body?.getReader();
          if (!reader) throw new Error('Failed to get response body');
          const chunks: Uint8Array[] = [];

          while (true) {
            const result = await watchdog.guard(reader.read());
            const { done, value } = result;
            if (done) break;
            const len = value?.byteLength ?? 0;
            downloadedSize += len || 1;
            downloadedArchive += len || 1;
            const now = Date.now();
            if (now - speedLastTime >= SPEED_UPDATE_INTERVAL_MS) {
              const elapsed = (now - speedLastTime) / 1000;
              if (elapsed > 0) {
                const bps = (downloadedSize - speedLastBytes) / elapsed;
                _useStates.setDownloadSpeedBpsRef.current(bps);
              }
              speedLastTime = now;
              speedLastBytes = downloadedSize;
            }
            if (!displayLeaderRef.current) {
              displayLeaderRef.current = desc;
              _useStates.setDownloadingFileName(desc);
            }
            if (now - lastFileDownloadUpdate >= PROGRESS_THROTTLE_MS && displayLeaderRef.current === desc) {
              lastFileDownloadUpdate = now;
              _useStates.setFileDownload(formatFileSize(totalArchive));
              _useStates.setFileDownloadProgress(formatFileSize(downloadedArchive));
            }
            updateProgress(downloadedArchive, totalArchive, name);
            if (displayLeaderRef.current === desc) updateProgress(downloadedArchive, totalArchive, 'progress');
            if (value) chunks.push(value);
          }

          progressLastCall[name] = 0;
          progressLastCall['progress'] = 0;
          if (displayLeaderRef.current === desc) {
            _useStates.setFileDownload(formatFileSize(totalArchive));
            _useStates.setFileDownloadProgress(formatFileSize(downloadedArchive));
            updateProgress(downloadedArchive, totalArchive, 'progress');
          }
          updateProgress(downloadedArchive, totalArchive, name);
          if (displayLeaderRef.current === desc) displayLeaderRef.current = null;

          tDownloadFim = Date.now();
          const tGravacao = Date.now();
          const blob = new Blob(chunks as BlobPart[], { type: 'application/zip' });
          chunks.length = 0;

          // O zip vai inteiro para a store `images_zip`, do jeito que chegou. Descompactar,
          // converter cada entrada em `Blob` e gravar imagem por imagem saiu do sync.
          const gravado = await gravarZipImagens(dataset, type, blob);

          downloadEvents[name] = { ...(downloadEvents[name] || {}), dataFim: getDateTimeMS(), status: 1 };
          const persisted = await persistDataset(type, dataset, null, { arquivosGravados: gravado ? 1 : 0 });

          logTempos(name, {
            bytes: blob.size,
            download: tDownloadFim - tArquivo,
            zipWrite: Date.now() - tGravacao,
            total: Date.now() - tArquivo,
          });

          return { ...persisted, name, datasetKey, type };
        } catch (error) {
          downloadEvents[name] = { ...(downloadEvents[name] || {}), dataFim: getDateTimeMS(), status: 0 };
          // Rede parada não melhora baixando o mesmo arquivo de novo como JSON: só dobraria a espera.
          if (isDownloadStalled(error)) throw error;
          console.warn(`Falha no ZIP de ${name}, tentando JSON. Motivo:`, error);
          return fetchAndSaveJson(url, name, desc, datasetKey, type, dataset, true);
        }
      };

      const tasks = (list ?? []).map(
        (dataset, index) => () =>
          fetchAndSaveJson(
            dataset.URL_S3,
            `${normalizeDatasetType(dataset.TIPO_ARQUIVO)}-${(dataset as DatasetWithStoreMeta).COD_LOJA ?? 'all'}-${(dataset as DatasetWithStoreMeta).COD_SEGMENTO ?? 'all'}-${index}`,
            getDatasetProgressLabel(dataset as DatasetWithStoreMeta),
            getDatasetKey(dataset, index),
            normalizeDatasetType(dataset.TIPO_ARQUIVO),
            dataset as DatasetWithStoreMeta
          )
      );

      _useStates.setDownloadingFiles((list ?? []).map((item) => getDatasetProgressLabel(item as DatasetWithStoreMeta)));

      const responses = await runWithConcurrency(tasks, MAX_CONCURRENT_DOWNLOADS);
      const concatDownloadEvents = Object.values(downloadEvents)
        .map((evt) => `${evt.status} ${evt.arquivo} ${evt.dataInicio?.split('T')[1] || ''}-${evt.dataFim?.split('T')[1] || ''}`)
        .join('|');

      void registerEvent({
        tipoEvento: 'DOWNLOAD',
        obsEvento: concatDownloadEvents,
        stEvento: Object.values(downloadEvents).some((evt) => evt.status === 0) ? 0 : 1,
        codRca: Number(getOnStorage('cod_pessoa') || 0),
      }).catch(() => {});

      const byDatasetKey: Record<string, DatasetDownloadResult> = {};
      const rejected: any[] = [];
      responses.forEach((item) => {
        if (item.status === 'fulfilled') byDatasetKey[item.value.datasetKey] = item.value;
        if (item.status === 'rejected') rejected.push(item.reason);
      });

      if (rejected.length > 0) {
        console.warn('Promises rejeitadas:', rejected);
      }

      _useStates.setDownloadSpeedBpsRef.current(0);
      if (!Object.keys(byDatasetKey).length) return undefined;
      return { byDatasetKey } as FetchDatasetsResponse;
    },
    []
  );

  return fetchDatasets;
};
